<template>
  <div class="answer-element">
    <div class="answer-header">
      <span class="answer-type-badge">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="9 17 4 12 9 7"></polyline>
          <path d="M20 18v-2a4 4 0 0 0-4-4H4"></path>
        </svg>
        <span>Réponse utilisateur</span>
      </span>
      <div class="element-actions">
        <button class="action-btn" @click.stop="$emit('duplicate')" title="Dupliquer">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
        </button>
        <button class="action-btn delete-btn" @click.stop="$emit('remove')" title="Supprimer">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
        </button>
      </div>
    </div>
    
    <div class="answer-content">
      <div class="form-group">
        <label>Types de réponse</label>
        <vue-multiselect
          v-model="selectedTypes"
          :options="answerTypes"  
          :multiple="true"
          :close-on-select="false"
          :clear-on-select="false"
          :preserve-search="true"
          placeholder="Sélectionner les types de réponse"
          :preselect-first="false"
          @input="handleTypeChange"
          @select="handleTypeChange"
          @remove="handleTypeChange"
        >
          <template v-slot:option="{ option }">
            <span>{{ getTypeLabel(option) }}</span>
          </template>
          <template v-slot:tag="{ option, remove }">
            <span class="multiselect-tag">
              <span class="multiselect-tag-text">{{ getTypeLabel(option) }}</span>
              <span class="multiselect-tag-remove" @mousedown.prevent.stop="remove(option)">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </span>
            </span>
          </template>
        </vue-multiselect>
      </div>
      
      <div class="form-group">
        <label>Variable</label>
        <VariableSelector 
          v-model="selectedVariable"
          :variables="variables"
          placeholder="Sélectionnez une variable"
          @change="handleVariableChange"
        />
      </div>
      
      <div class="form-group">
        <label>Réponse par défaut</label>
        <div class="input-wrapper">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="tag-icon">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
          <input 
            type="text" 
            v-model="defaultReply"
            class="default-reply-input"
            placeholder="Réponse par défaut en cas d'erreur"
            @input="handleDefaultReplyChange"
          >
        </div>
      </div>
      
      <div class="form-group">
        <label class="ai-card" :class="{ 'is-active': aiValidation }">
          <div class="ai-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 3l1.9 5.8a2 2 0 0 0 1.3 1.3L21 12l-5.8 1.9a2 2 0 0 0-1.3 1.3L12 21l-1.9-5.8a2 2 0 0 0-1.3-1.3L3 12l5.8-1.9a2 2 0 0 0 1.3-1.3L12 3z"></path>
              <path d="M19 3v2"></path>
              <path d="M20 4h-2"></path>
              <path d="M5 19v2"></path>
              <path d="M6 20H4"></path>
            </svg>
          </div>
          <div class="ai-card-text">
            <div class="ai-card-title">
              <span>Validation intelligente (IA)</span>
              <span class="ai-status">
                <span class="ai-status-dot"></span>
                {{ aiValidation ? 'Active' : 'Inactive' }}
              </span>
            </div>
            <div class="ai-card-hint">
              L'IA reformate les réponses ambiguës et relance l'utilisateur en cas d'erreur.
            </div>
          </div>
          <div class="ai-toggle">
            <input
              type="checkbox"
              v-model="aiValidation"
              @change="handleAiValidationChange"
            >
            <span class="ai-toggle-track">
              <span class="ai-toggle-thumb">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </span>
            </span>
          </div>
        </label>
      </div>

      <div class="answer-description">
        Configure la façon dont l'utilisateur peut répondre
      </div>
    </div>
  </div>
</template>

<script>
import VueMultiselect from 'vue-multiselect'
import VariableSelector from '@/components/common/VariableSelector.vue'
import 'vue-multiselect/dist/vue-multiselect.css'

export default {
  name: 'AnswerElement',
  components: {
    VueMultiselect,
    VariableSelector
  },
  props: {
    element: {
      type: Object,
      required: true
    },
    variables: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      selectedTypes: this.getInitialTypes(),
      selectedVariable: this.getInitialVariable(),
      defaultReply: this.getInitialDefaultReply(),
      aiValidation: this.getInitialAiValidation(),
      defaultReplyTimeout: null,
      answerTypes: [
        'text',
        'interactive',
        'audio',
        'video',
        'image',
        'document',
        'tel_sen',
        'phone_number',
        'alphabet',
        'email',
        'number',
        'calendar',
        'location'
      ]
    }
  },
  methods: {
    getInitialTypes() {
      return this.element.options?.answer_type || ['text'];
    },
    getTypeLabel(type) {
      const labels = {
        'text': 'Texte',
        'interactive': 'Interactif',
        'audio': 'Audio',
        'tel_sen': 'Téléphone sénégalais',
        'phone_number': 'Numéro de téléphone',
        'alphabet': 'Alphabétique',
        'email': 'Email',
        'number': 'Nombre',
        'calendar': 'Calendrier (Web Sim Only)',
        'location': 'Localisation'
      };
      return labels[type] || type;
    },
    getInitialVariable() {
      return this.element.options?.variable?.id || '';
    },
    getInitialDefaultReply() {
      return this.element.options?.default_reply || '';
    },
    getInitialAiValidation() {
      // Active par défaut : seul ai_validation: false la désactive (opt-out)
      return this.element.options?.ai_validation !== false;
    },
    handleAiValidationChange() {
      this.$emit('update', {
        ...this.element,
        options: {
          ...this.element.options,
          ai_validation: this.aiValidation
        }
      });
    },
    handleTypeChange() {
      this.$emit('update', {
        ...this.element,
        options: {
          ...this.element.options,
          answer_type: this.selectedTypes
        }
      });
    },
    handleVariableChange() {
      this.$emit('update', {
        ...this.element,
        options: {
          ...this.element.options,
          variable: { id: this.selectedVariable }
        }
      });
    },
    handleDefaultReplyChange() {
      // Annuler le timeout précédent s'il existe
      if (this.defaultReplyTimeout) {
        clearTimeout(this.defaultReplyTimeout);
      }
      
      // Créer un nouveau timeout de 1.5 seconde
      this.defaultReplyTimeout = setTimeout(() => {
        this.$emit('update', {
          ...this.element,
          options: {
            ...this.element.options,
            default_reply: this.defaultReply
          }
        });
      }, 1500);
    }
  },
  
  beforeUnmount() {
    // Nettoyer le timeout en cas de destruction du composant
    if (this.defaultReplyTimeout) {
      clearTimeout(this.defaultReplyTimeout);
    }
  }
}
</script>

<style>
@import 'vue-multiselect/dist/vue-multiselect.css';
</style>

<style scoped>
.answer-element {
  background: #ffffff;
  border-radius: 16px;
  overflow: visible;
  box-shadow: 0 10px 25px rgba(20, 48, 40, 0.08);
  border: 1px solid rgba(20, 48, 40, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-bottom: 20px;
  position: relative;
  z-index: 1;
}

.answer-element:hover {
  box-shadow: 0 15px 35px rgba(20, 48, 40, 0.12);
  border-color: rgba(218, 255, 209, 0.5);
  transform: translateY(-2px);
}

.answer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  background: #143028; /* Peelo Dark Green */
  border-bottom: 1px solid rgba(218, 255, 209, 0.1);
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
}

.answer-type-badge {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  font-weight: 700;
  color: #daffd1; /* Peelo Mint */
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.answer-type-badge svg {
  color: #daffd1;
}

.element-actions {
  display: flex;
  gap: 10px;
}

.action-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(218, 255, 209, 0.15);
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(218, 255, 209, 0.7);
  transition: all 0.25s ease;
}

.action-btn:hover {
  background: rgba(218, 255, 209, 0.1);
  color: #daffd1;
  border-color: #daffd1;
  transform: scale(1.05);
}

.action-btn.delete-btn:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #f87171;
  border-color: #ef4444;
}

.answer-content {
  padding: 1.25rem;
  overflow: visible;
  position: relative;
}

.form-group {
  margin-bottom: 1.25rem;
}

.form-group label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
}

.select-wrapper,
.input-wrapper {
  position: relative;
  z-index: 1;
}

.tag-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
  z-index: 1;
}

.variable-select,
.default-reply-input {
  width: 100%;
  padding: 0.625rem 1rem 0.625rem 2.25rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  background: white;
  color: #1e293b;
  transition: all 0.2s ease;
  position: relative;
  z-index: 1;
}

.variable-select:hover,
.default-reply-input:hover {
  border-color: rgba(20, 48, 40, 0.3);
}

.variable-select:focus,
.default-reply-input:focus {
  outline: none;
  border-color: #143028;
  box-shadow: 0 0 0 3px rgba(20, 48, 40, 0.1);
}

.answer-description {
  color: #64748b;
  font-size: 0.875rem;
  margin-top: 0.75rem;
}

/* ===== Carte Validation intelligente (IA) ===== */

.ai-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  user-select: none;
  position: relative;
  overflow: hidden;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.ai-card:hover {
  border-color: #cbd5e1;
  transform: translateY(-1px);
}

.ai-card.is-active {
  background: linear-gradient(135deg, #143028 0%, #1d4438 100%);
  border-color: #143028;
  box-shadow: 0 10px 24px rgba(20, 48, 40, 0.28);
}

/* halo lumineux menthe dans le coin de la carte active */
.ai-card.is-active::after {
  content: '';
  position: absolute;
  top: -40%;
  right: -10%;
  width: 140px;
  height: 140px;
  background: radial-gradient(circle, rgba(218, 255, 209, 0.18) 0%, transparent 70%);
  pointer-events: none;
}

.ai-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #eef2f7;
  color: #94a3b8;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.ai-card.is-active .ai-icon {
  background: rgba(218, 255, 209, 0.12);
  color: #daffd1;
  box-shadow: inset 0 0 0 1px rgba(218, 255, 209, 0.25);
}

.ai-card.is-active .ai-icon svg {
  animation: ai-sparkle-pop 0.45s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes ai-sparkle-pop {
  0% { transform: scale(0.5) rotate(-20deg); }
  60% { transform: scale(1.15) rotate(8deg); }
  100% { transform: scale(1) rotate(0); }
}

.ai-card-text {
  flex: 1;
  min-width: 0;
}

.ai-card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 3px;
  transition: color 0.3s ease;
}

.ai-card.is-active .ai-card-title {
  color: #ffffff;
}

.ai-status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 2px 8px;
  border-radius: 999px;
  background: #e2e8f0;
  color: #64748b;
  transition: all 0.3s ease;
}

.ai-card.is-active .ai-status {
  background: rgba(218, 255, 209, 0.15);
  color: #daffd1;
}

.ai-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #94a3b8;
  transition: background 0.3s ease;
}

.ai-card.is-active .ai-status-dot {
  background: #4ade80;
  animation: ai-dot-pulse 1.8s ease-in-out infinite;
}

@keyframes ai-dot-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(74, 222, 128, 0.5); }
  50% { box-shadow: 0 0 0 4px rgba(74, 222, 128, 0); }
}

.ai-card-hint {
  font-size: 0.78rem;
  color: #64748b;
  line-height: 1.4;
  transition: color 0.3s ease;
}

.ai-card.is-active .ai-card-hint {
  color: rgba(218, 255, 209, 0.75);
}

.ai-toggle {
  position: relative;
  width: 46px;
  height: 26px;
  flex-shrink: 0;
}

.ai-toggle input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.ai-toggle-track {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 999px;
  background: #cbd5e1;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.ai-toggle-thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: transparent;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.ai-toggle input:checked + .ai-toggle-track {
  background: #daffd1;
  box-shadow: 0 0 12px rgba(218, 255, 209, 0.45);
}

.ai-toggle input:checked + .ai-toggle-track .ai-toggle-thumb {
  transform: translateX(20px);
  background: #143028;
  color: #daffd1;
}

.multiselect-tag {
  background: #daffd1;
  color: #143028;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-size: 0.8rem;
  margin: 2px 4px 2px 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  border: 1px solid rgba(20, 48, 40, 0.1);
}

.multiselect-tag-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0.6;
  border-radius: 50%;
  padding: 2px;
  transition: all 0.2s ease;
}

.multiselect-tag-remove:hover {
  opacity: 1;
  background: rgba(20, 48, 40, 0.1);
  color: #ef4444;
}

/* Styles pour le multiselect dropdown */
.form-group {
  position: relative;
  z-index: 10;
}

.form-group:has(.multiselect--active) {
  z-index: 10000 !important;
}

.multiselect {
  z-index: 9999 !important;
  position: relative !important;
}

.multiselect__content-wrapper {
  z-index: 10000 !important;
  position: absolute !important;
  top: 100% !important;
  left: 0 !important;
  right: 0 !important;
}

.multiselect__content {
  z-index: 10001 !important;
  background: white !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px !important;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15) !important;
}

/* S'assurer que les autres form-group ont un z-index inférieur quand le multiselect est actif */
.answer-content:has(.multiselect--active) .form-group:not(:has(.multiselect--active)) {
  z-index: 1 !important;
}

.answer-content:has(.multiselect--active) .select-wrapper,
.answer-content:has(.multiselect--active) .input-wrapper {
  z-index: 1 !important;
}

.answer-content:has(.multiselect--active) .variable-select,
.answer-content:has(.multiselect--active) .default-reply-input {
  z-index: 1 !important;
}

</style>
